import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/ui/rich-text-editor";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AttachmentUpload from "@/components/emails/AttachmentUpload";
import { EmailTemplate } from "@/lib/types";
import { env } from "@/lib/environment";

// Updated AttachmentFile interface to match ComposeEmail
interface AttachmentFile {
  id: string;
  file?: File; // Only for new uploads
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  url?: string; // For preview or download
  serverId?: string; // For server-provided attachment ID
  // Server fields for existing attachments
  attachment_id?: number;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  upload_date?: string;
  storage_path?: string;
  isExisting?: boolean; // Flag to distinguish existing vs new files
}

// Note: EmailTemplate is now imported from @/lib/types

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: EmailTemplate | null; // For editing existing templates
  mode?: 'create' | 'edit';
}

export default function TemplateModal({ 
  isOpen, 
  onClose, 
  template = null,
  mode = 'create'
}: TemplateModalProps) {
  const [formData, setFormData] = useState<EmailTemplate>({
    name: "",
    subject: "",
    content: "",
    category: "",
    tags: [],
  });
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  // Remove all tag-related state and handlers
  // Remove tags from formData, setFormData, handleInputChange, and UI
  // Change category input to span full width (remove grid, use single column)
  const [focusedField, setFocusedField] = useState<'subject' | 'content'>('content');

  // 1. Update the variable list to:
  const emailVariables = [
    'PlayerName',
    'PlayerFirstName',
    'PlayerLastName',
    'PlayerPosition',
    'PlayerClub',
    'PlayerLevel',
  ];

  // 2. Add refs for subject and content fields
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const contentTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const richTextEditorRef = useRef<any>(null);

  // 3. Update insertVariable to insert at cursor position for both subject and content
  const insertVariable = (variable: string) => {
    const variableText = `{{${variable}}}`;
    if (focusedField === 'subject') {
      const input = subjectInputRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = formData.subject.slice(0, start) + variableText + formData.subject.slice(end);
        setFormData(prev => ({ ...prev, subject: newValue }));
        setTimeout(() => {
          // Don't automatically focus - let user control where they want to insert
          input.setSelectionRange(start + variableText.length, start + variableText.length);
        }, 0);
        return;
      }
    } else {
      // Use RichTextEditor ref for cursor-aware insertion
      if (richTextEditorRef.current && typeof richTextEditorRef.current.insertVariable === 'function') {
        richTextEditorRef.current.insertVariable(variable);
      } else {
        setFormData(prev => ({ ...prev, content: prev.content + variableText }));
      }
    }
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  // Predefined categories for suggestions
  const predefinedCategories = [
    "Recruitment",
    "Follow-up", 
    "Offers",
    "Scholarship",
    "Events",
    "General",
    "Welcome",
    "Rejection",
    "Thank You"
  ];

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (isOpen) {
      if (template && mode === 'edit') {
        setFormData({
          ...template,
          // Ensure all fields have proper string values, not null
          name: template.name || "",
          subject: template.subject || "",
          content: template.content || "",
          category: template.category || "",
          tags: template.tags || [],
        });
        
        // Load template attachments in Gmail-like format
        const loadTemplateAttachments = async () => {
          try {
            if (template.id) {
              console.log(`Loading attachments for template ${template.id}`);
              const response = await apiRequest("GET", `/api/attachments/template/${template.id}`);
              console.log('Template attachments response:', response.status, response.statusText);
              
              if (response.ok) {
                const data = await response.json();
                console.log('Template attachments data:', data);
                
                if (data.success && data.attachments && data.attachments.length > 0) {
                  // Convert template attachments to AttachmentFile format
                  const templateAttachments: AttachmentFile[] = data.attachments.map((attachment: any) => ({
                    id: `template-${attachment.id}`,
                    name: attachment.file_name,
                    size: attachment.file_size,
                    type: attachment.file_type,
                    progress: 100,
                    status: 'completed' as const,
                    attachment_id: attachment.id,
                    file_name: attachment.file_name,
                    file_size: attachment.file_size,
                    file_type: attachment.file_type,
                    upload_date: attachment.upload_date,
                    isExisting: true,
                    serverId: attachment.id.toString()
                  }));
                  
                  console.log('Setting template attachments:', templateAttachments);
                  setAttachments(templateAttachments);
                  
                  toast({
                    title: "Template loaded",
                    description: `Template loaded with ${templateAttachments.length} attachment${templateAttachments.length !== 1 ? 's' : ''}`,
                  });
                } else {
                  console.log('No attachments found for template or response not successful');
                  setAttachments([]);
                }
              } else {
                console.error('Failed to load template attachments:', response.status, response.statusText);
                setAttachments([]);
              }
            }
          } catch (error) {
            console.error('Error loading template attachments:', error);
            setAttachments([]);
          }
        };
        
        loadTemplateAttachments();
      } else {
        // Reset for new template
        setFormData({
          name: "",
          subject: "",
          content: "",
          category: "",
          tags: [],
        });
        setAttachments([]); // Reset attachments for new template
      }
      setErrors({});
    } else {
      // Reset attachments when modal closes (Gmail-like behavior)
      setAttachments([]);
    }
  }, [isOpen, template, mode, toast]);

  const createTemplateMutation = useMutation({
    mutationFn: async (templateData: EmailTemplate) => {
      const endpoint = mode === 'edit' && template?.id 
        ? `/api/email-templates/${template.id}`
        : "/api/email-templates/";
      const method = mode === 'edit' ? "PUT" : "POST";
      
      // First create/update the template
      const response = await apiRequest(method, endpoint, {
        name: templateData.name,
        subject: templateData.subject,
        content: templateData.content,
        category: templateData.category || null,
        tags: templateData.tags || [],
      });
      
      const result = await response.json();
      const templateId = result.id || template?.id;
      
      console.log('Template save result:', result);
      console.log('Extracted template ID:', templateId);
      console.log('Mode:', mode);
      console.log('Attachments to associate:', attachments.filter(att => att.status === 'completed' && att.attachment_id));
      
      // Handle attachment associations if there are any completed attachments
      const completedAttachments = attachments.filter(att => 
        att.status === 'completed' && att.attachment_id
      );
      
      if (completedAttachments.length > 0 && templateId) {
        try {
          // Associate attachments with the template
          const attachmentIds = completedAttachments.map(att => att.attachment_id);
          
          console.log('Starting attachment association process...');
          console.log('Template ID for association:', templateId);
          console.log('Attachment IDs to associate:', attachmentIds);
          
          const formData = new FormData();
          formData.append('template_id', templateId.toString());
          formData.append('attachment_ids', JSON.stringify(attachmentIds));
          
          const sessionId = localStorage.getItem('sessionId');
          
          console.log('Making attachment association request...');
          const attachmentResponse = await fetch(`${env.apiUrl}/api/attachments/associate-with-template`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionId}`,
            },
            body: formData,
          });
          
          if (!attachmentResponse.ok) {
            const errorText = await attachmentResponse.text();
            console.error('Failed to associate attachments with template:', errorText);
            throw new Error(`Failed to save template attachments: ${errorText}`);
          }
          
          const associationResult = await attachmentResponse.json();
          console.log('Attachment association successful:', associationResult);
          console.log(`Associated ${attachmentIds.length} attachments with template ${templateId}`);
        } catch (error) {
          console.error('Error associating attachments:', error);
          // Don't fail the entire operation, but log the error
          throw error;
        }
      } else if (completedAttachments.length > 0 && !templateId) {
        console.error('Cannot associate attachments: templateId is missing');
        console.error('completedAttachments:', completedAttachments);
        console.error('templateId:', templateId);
      }
      
      return result;
    },
    onSuccess: () => {
      const attachmentCount = attachments.filter(att => att.status === 'completed').length;
      const attachmentText = attachmentCount > 0 ? ` with ${attachmentCount} attachment${attachmentCount !== 1 ? 's' : ''}` : '';
      
      toast({
        title: mode === 'edit' ? "Template updated" : "Template created",
        description: `Email template has been successfully ${mode === 'edit' ? 'updated' : 'created'}${attachmentText}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates/"] });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: `Failed to ${mode === 'edit' ? 'update' : 'create'} template`,
        description: error.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Template name is required";
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    
    if (!formData.content.trim()) {
      newErrors.content = "Template content is required";
    }
    
    // Check for uploading attachments
    const uploadingAttachments = attachments.filter(att => att.status === 'uploading');
    if (uploadingAttachments.length > 0) {
      newErrors.attachments = "Please wait for all attachments to finish uploading";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    
    // Additional check for uploading attachments
    const uploadingAttachments = attachments.filter(att => att.status === 'uploading');
    if (uploadingAttachments.length > 0) {
      toast({
        title: "Upload in progress",
        description: "Please wait for all attachments to finish uploading before saving",
        variant: "destructive",
      });
      return;
    }
    
    createTemplateMutation.mutate(formData);
  };

  const handleInputChange = (field: keyof EmailTemplate, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Template' : 'Create New Template'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold w-6 h-6 flex items-center justify-center"
            disabled={createTemplateMutation.isPending}
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Template Name */}
            <div>
              <Label htmlFor="template-name" className="text-sm font-medium">
                Template Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="template-name"
                value={formData.name || ""}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`mt-1 ${errors.name ? "border-red-500" : ""}`}
                placeholder="e.g., Initial Interest, Follow-up, Scholarship Offer"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Category and Tags Row */}
            <div className="mb-6">
              <Label htmlFor="template-category" className="text-sm font-medium">Category</Label>
              <div className="mt-1 relative">
                <Input
                  id="template-category"
                  value={formData.category || ""}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  placeholder="Select or type a category"
                  list="categories"
                />
                <datalist id="categories">
                  {predefinedCategories.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Choose from suggestions or create your own
              </p>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="template-subject" className="text-sm font-medium">
                Subject Line <span className="text-red-500">*</span>
              </Label>
              <Input
                id="template-subject"
                value={formData.subject || ""}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className={`mt-1 ${errors.subject ? "border-red-500" : ""}`}
                placeholder="e.g., Interest in your soccer talent - {{SchoolName}}"
                onFocus={() => setFocusedField('subject')}
                ref={subjectInputRef}
              />
              {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Use variables like "PlayerName", "SchoolName", etc.
              </p>
            </div>

            {/* Content */}
            <div onClick={() => setFocusedField('content')}>
              <Label className="text-sm font-medium">
                Template Content <span className="text-red-500">*</span>
              </Label>
              <div className="mt-1">
                <RichTextEditor
                  content={formData.content || ""}
                  onChange={(value) => handleInputChange('content', value)}
                  placeholder="Write your email template here... Use variables like {{PlayerName}} to personalize emails."
                  ref={richTextEditorRef}
                />
              </div>
              {errors.content && <p className="text-red-500 text-xs mt-1">{errors.content}</p>}
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Attachments</Label>
                <span className="text-xs text-gray-500">
                  Optional files to include with this template
                </span>
              </div>
              
              {/* Upload status indicator */}
              {attachments.some(att => att.status === 'uploading') && (
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-blue-700">
                      Uploading attachments... Please wait before saving.
                    </span>
                  </div>
                </div>
              )}
              
              <AttachmentUpload
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                maxFileSize={25 * 1024 * 1024} // 25MB
                maxTotalSize={100 * 1024 * 1024} // 100MB
                maxFiles={10}
                disabled={createTemplateMutation.isPending}
                showExisting={false} // Gmail-like behavior: only show current session attachments
              />
              
              {errors.attachments && (
                <p className="text-red-500 text-xs mt-1">{errors.attachments}</p>
              )}
            </div>

            {/* Variable Helper */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                <i className="fas fa-info-circle mr-2"></i>
                Available Variables
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {emailVariables.map((variable) => (
                  <code
                    key={variable}
                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded cursor-pointer hover:bg-blue-200 transition-colors"
                    onClick={() => {
                      insertVariable(variable);
                    }}
                  >
                    {variable}
                  </code>
                ))}
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Click any variable to add it to your template content
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={createTemplateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={createTemplateMutation.isPending}
          >
            {createTemplateMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {mode === 'edit' ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              mode === 'edit' ? 'Update Template' : 'Create Template'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}